import { useState, useCallback, useEffect, useRef } from "react"
import { ServerDate } from "helpers/server-date.class"

type AMPMType = "" | "pm" | "am"

export type FormattedTimeFromMillisecondsType = {
  milliseconds: number
  seconds: number
  minutes: number
  hours: number
  ampm?: AMPMType
}

class Validate {
  static expiryTimestamp(expiryTimestamp: Date) {
    const isValid = new ServerDate(expiryTimestamp).getTime() > 0
    if (!isValid) {
      console.warn(
        "react-timer-hook: { useTimer } Invalid expiryTimestamp settings",
        expiryTimestamp
      )
    }
    return isValid
  }

  static onExpire(onExpire: () => void) {
    const isValid = onExpire && typeof onExpire === "function"
    if (onExpire && !isValid) {
      console.warn(
        "react-timer-hook: { useTimer } Invalid onExpire settings function",
        onExpire
      )
    }
    return isValid
  }
}

class Time {
  static getTimeFromMilliseconds(
    millisecs: number,
    isCountDown = true
  ): TimeFromMillisecondsType {
    const totalSeconds = isCountDown
      ? Math.ceil(millisecs / 1000)
      : Math.floor(millisecs / 1000)
    const days = Math.floor(totalSeconds / (60 * 60 * 24))
    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60))
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60)
    const seconds = Math.floor(totalSeconds % 60)
    const milliseconds = Math.floor(millisecs % 1000)

    return {
      totalMilliseconds: millisecs,
      totalSeconds,
      milliseconds,
      seconds,
      minutes,
      hours,
      days
    }
  }

  static getMillisecondsFromExpiry(expiry: Date): number {
    const now = new ServerDate().getTime()
    const milliSecondsDistance = expiry?.getTime() - now
    return milliSecondsDistance > 0 ? milliSecondsDistance : 0
  }

  static getMillisecondsFromPrevTime(prevTime: number): number {
    const now = new ServerDate().getTime()
    const milliSecondsDistance = now - prevTime
    return milliSecondsDistance > 0 ? milliSecondsDistance : 0
  }

  static getMillisecondsFromTimeNow(): number {
    const now = new ServerDate()
    const currentTimestamp = now.getTime()
    const offset = now.getTimezoneOffset() * 60 * 1000
    return currentTimestamp - offset
  }

  static getFormattedTimeFromMilliseconds(
    milliseconds: number,
    format?: "12-hour"
  ): FormattedTimeFromMillisecondsType {
    const {
      milliseconds: millisecVal,
      seconds: secondsValue,
      minutes,
      hours
    } = Time.getTimeFromMilliseconds(milliseconds)
    let ampm: AMPMType = ""
    let hoursValue = hours

    if (format === "12-hour") {
      ampm = hours >= 12 ? "pm" : "am"
      hoursValue = hours % 12
    }

    return {
      milliseconds: millisecVal,
      seconds: secondsValue,
      minutes,
      hours: hoursValue,
      ampm
    }
  }
}

const SECOND_INTERVAL = 1000

export type TimeFromMillisecondsType = {
  totalMilliseconds: number
  totalSeconds: number
  milliseconds: number
  seconds: number
  minutes: number
  hours: number
  days: number
}

export type useTimerSettingsType = {
  expiryTimestamp: Date
  onExpire?: () => void
  autoStart?: boolean
  interval?: number
}

export type useTimerResultType = TimeFromMillisecondsType & {
  start: () => void
  pause: () => void
  resume: () => void
  restart: (newExpiryTimestamp: Date, newAutoStart?: boolean) => void
  isRunning: boolean
}

function useInterval(callback: () => void, delay: number | null) {
  const callbackRef = useRef(callback)

  useEffect(() => {
    callbackRef.current = callback
  })

  useEffect(() => {
    if (!delay) {
      return () => {}
    }

    const interval = setInterval(() => {
      callbackRef?.current?.()
    }, delay)
    return () => clearInterval(interval)
  }, [delay])
}

export function useServerTimer({
  expiryTimestamp: expiry,
  onExpire = () => {},
  autoStart = true,
  interval: customInterval = SECOND_INTERVAL
}: useTimerSettingsType): useTimerResultType {
  const [expiryTimestamp, setExpiryTimestamp] = useState(new ServerDate(expiry))
  const [milliseconds, setMilliseconds] = useState(
    Time.getMillisecondsFromExpiry(expiryTimestamp)
  )
  const [isRunning, setIsRunning] = useState(autoStart)
  const [didStart, setDidStart] = useState(autoStart)
  const [interval, setInterval] = useState<number | null>(customInterval)

  const handleExpire = useCallback(() => {
    if (Validate.onExpire(onExpire)) {
      onExpire()
    }
    setIsRunning(false)
    setInterval(null)
  }, [onExpire])

  const pause = useCallback(() => {
    setIsRunning(false)
  }, [])

  const restart = useCallback(
    (newExpiryTimestamp: Date, newAutoStart = true) => {
      setInterval(customInterval)
      setDidStart(newAutoStart)
      setIsRunning(newAutoStart)
      setExpiryTimestamp(newExpiryTimestamp)
      setMilliseconds(Time.getMillisecondsFromExpiry(newExpiryTimestamp))
    },
    [customInterval]
  )

  const resume = useCallback(() => {
    const time = new ServerDate()

    time.setMilliseconds(time.getMilliseconds() + milliseconds)

    restart(time)
  }, [milliseconds, restart])

  const start = useCallback(() => {
    if (didStart) {
      setMilliseconds(Time.getMillisecondsFromExpiry(expiryTimestamp))
      setIsRunning(true)
    } else {
      resume()
    }
  }, [expiryTimestamp, didStart, resume])

  useInterval(
    () => {
      const millisecondsValue = Time.getMillisecondsFromExpiry(expiryTimestamp)
      setMilliseconds(millisecondsValue)

      if (millisecondsValue <= 0) {
        handleExpire()
      } else if (interval && millisecondsValue < interval) {
        setInterval(millisecondsValue)
      }
    },
    isRunning ? interval : null
  )

  useEffect(() => {
    Validate.expiryTimestamp(expiryTimestamp)
  }, [expiryTimestamp])

  return {
    ...Time.getTimeFromMilliseconds(milliseconds),
    start,
    pause,
    resume,
    restart,
    isRunning
  }
}

export default useServerTimer
